/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  effect,
  Injector,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, skip } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { SidebarComponent, NebulaNavigationService, MenuItem, BreadcrumbItem } from 'nebula-ui-kit';
import { SidebarService } from '../../../../data-access/src/lib/sidebar.service';
import { SidebarStateService } from '../../../../data-access/src/lib/sidebar-state.service';
import { SidebarMenuItem } from '../../../../domain/src/lib/sidebar.model';
import { BreadcrumbSyncService } from '../../../../data-access/src/lib/breadcrumb-sync.service';
import { isRouteValid, getModuleFromRoute } from '../../../../utils/src/lib/route-validator';
import { RouteSyncService } from '../../../../utils/src/lib/route-sync';
import { MAX_OPEN_TABS } from '../../../../const/src/lib/layout.constants';
import { MenuIconService, TranslationService } from '@nebula-shared/data-access';

/**
 * Etiquetas por segmento de ruta que uso este sidebar antes de i18n.
 *
 * @deprecated Ya no se consulta: el breadcrumb resuelve por
 * `SidebarService.labelForSegment`. Se conserva como registro de las
 * etiquetas de entonces; las nuevas van al bundle, no aqui.
 */
export const SIDEBAR_ROUTE_SEGMENT_LABELS: Record<string, string> = {
  accounting: 'Contabilidad',
  tesorería: 'Tesorería',
  budget: 'Presupuesto',
  masters: 'Maestros',
  'cost-center': 'Centros de Costo',
  'cost-center-type': 'Tipos de Centro de Costo',
  'department-city': 'Departamentos y Ciudades',
  list: 'Listado',
  create: 'Crear',
  edit: 'Editar',
  detail: 'Detalle',
  view: 'Ver',
  dashboard: 'Inicio',
  ingest: 'Carga de Documentos',
  preservation: 'Preservacion Digital',
  retention: 'Retencion Documental',
  'legal-hold': 'Custodia Legal ',
  reports: 'Reportes Generales',
  cloud: 'Respaldo Externo',
  notifications: 'Notificaciones',
  audit: 'Historial de Actividad',
  settings: 'Configuracion General',
  admin: 'Administrador',
  tenants: 'Gestion de Empresas',
  permissions: 'Permisos',
  servers: 'Configuracion Servidores',
  'preservation-messages': 'Mensajes Informativos',
  'repository-admin': 'Boveda Digital',
  'accounting-parameters': 'Parámetros',
  'tipo-plan': 'Tipos de Plan',
  'tipo-plan-historico': 'Histórico de Tipos de Plan',
  dependency: 'Dependencias',
  'dian-concept': 'Conceptos Dian',
  'account-structure': 'Estructura de Cuentas',
  'concept-entry-type': 'Conceptos/Tipo Asiento',
  currency: 'Monedas',
  'account-plan': 'Plan Cuentas',
  'third-party': 'Terceros',
  'accounting-format': 'Formatos Contables',
  accrual: 'Causación Contable',
  'deferred-process': 'Proceso de Diferidos',
  'journal-entry': 'Asiento Contable',
  'accrual-approval/list': 'Aprobar Causación',
  'accrual-annulment/list': 'Anular Causación',
  'treasury-parameters': 'Parámetros',
  depreciation: 'Depreciación',
  'journal-entry-annulment': 'Anulación Asientos',
  'bank-account': 'Cuentas Bancarias',
  'other-discount': 'Otros Descuentos',
  'other-deductions': 'Otras Deducciones',
  deferred: 'Diferidos',
  'journal-entry-deferred': 'Diferidos',
  'accounting-concepts': 'Conceptos',
  'journal-entry-type': 'Tipo Asientos',
  'accounting-currency': 'Monedas',
  'exchange-rate': 'Tasas de Cambio',
  'treasury-third-party': 'Terceros',
  'payment-annulment': 'Anulación Pagos',
  'bank-payment': 'Pagos Bancarios',
  'expense-voucher': 'Comprobante Egreso',
  'income-voucher': 'Comprobante Ingreso',
  'income-annulment': 'Anulación Ingresos',
  invoice: 'Factura',
  parameters: 'Parámetros de Facturación',
  'concepts-revenue': 'Revenue Concepts',
  'payment-file-scructure': 'Escructura de Pago',
  'payment-files': 'Archivos de Pago',
  'processing-of-bank': 'Traslados Bancarios',
  'processing-of-bank-cancellation': 'Traslados Bancarios',
};

@Component({
  selector: 'app-sidebar-wrapper',
  standalone: true,
  imports: [CommonModule, SidebarComponent],
  template: `
    <lib-sidebar
      [menuItems]="menuItems"
      [collapsed]="collapsed()"
      [isLoading]="isLoading"
      [logoUrl]="logoUrl()"
      [logoAlt]="logoAlt()"
      (itemClick)="onItemClick($event)"
      (collapsedChange)="onToggleSidebar()"
      [title]="sidebarTitle()"
    />

    @if (toastMsg) {
      <div class="toast-container" role="status" aria-live="polite">
        <span class="toast-message">{{ toastMsg }}</span>
      </div>
    }
  `,
  styleUrls: ['./sidebar-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarWrapperComponent implements OnInit, OnDestroy {
  readonly collapsed = input<boolean>(false);
  readonly collapsedChange = output<boolean>();
  readonly routeSelected = output<string>();
  readonly logoUrl = input<string | undefined>();
  readonly logoAlt = input<string>('Nebula App');

  readonly sidebarService = inject(SidebarService);
  private readonly translation = inject(TranslationService);
  readonly router = inject(Router);
  readonly navService = inject(NebulaNavigationService);
  readonly sidebarState = inject(SidebarStateService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly injector = inject(Injector);
  readonly breadcrumbSync = inject(BreadcrumbSyncService);

  readonly destroy$ = new Subject<void>();
  private currentProductId: string | null = null;
  private reloadTimeout: any = null;
  private initialLoadDone = false;

  toastMsg: string | null = null;
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;
  readonly routeSync = inject(RouteSyncService);
  private readonly menuIconService = inject(MenuIconService);

  readonly sidebarTitle = computed(() => {
    const currentModule = localStorage.getItem('selectedRoute') || 'accounting';

    const specificTitles: Record<string, string> = {
      vault: 'Nebula Vault',
      invoicing: 'Facturación',
    };
    return specificTitles[currentModule] || 'Nebula ERP';
  });
  isLoading = false;
  menuItems: SidebarMenuItem[] = [];

  ngOnInit(): void {
    this.loadMenu();
    this.listenToProductChanges();
    this.listenToRouteChanges();
    this.listenToLanguageChanges();
  }

  /** Recarga el menu cuando cambia el idioma para re-traducir las etiquetas (omite el valor inicial). */
  private listenToLanguageChanges(): void {
    this.translation.currentLang$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
      this.loadMenu();
    });
  }

  private showSimpleToast(msg: string, ms = 1500) {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }

    this.toastMsg = msg;
    this.cdr.markForCheck();

    this.toastTimeout = setTimeout(() => {
      this.toastMsg = null;
      this.toastTimeout = null;
      this.cdr.markForCheck();
    }, ms);
  }

  listenToProductChanges(): void {
    effect(
      () => {
        const productId = localStorage.getItem('selectedProductId');
        if (productId !== this.currentProductId) {
          this.currentProductId = productId;
          if (this.initialLoadDone) {
            this.reloadMenuWithDelay();
          }
        }
      },
      { injector: this.injector },
    );
  }

  listenToRouteChanges(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const newProductId = localStorage.getItem('selectedProductId');
        if (newProductId !== this.currentProductId && this.initialLoadDone) {
          this.currentProductId = newProductId;
          this.reloadMenuWithDelay();
        }
      }
    });
  }

  reloadMenuWithDelay(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.reloadTimeout = setTimeout(() => {
      this.reloadMenu();
    }, 150);
  }

  reloadMenu(): void {
    this.menuItems = [];
    this.isLoading = true;
    this.cdr.markForCheck();
    this.loadMenu();
  }

  loadMenu(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.sidebarService
      .getMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SidebarMenuItem[]) => {
          this.menuItems = this.processMenuRoutesStatic(data);
          this.isLoading = false;
          this.initialLoadDone = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.initialLoadDone = true;
          this.cdr.markForCheck();
        },
      });
  }

  processMenuRoutesStatic(menu: SidebarMenuItem[]): SidebarMenuItem[] {
    const processItem = (item: SidebarMenuItem): SidebarMenuItem => {
      const processed = { ...item };

      if (processed.route) {
        processed.route = this.normalizeRouteStatic(processed.route);
      }

      if (processed.children?.length) {
        processed.children = processed.children.map((child) => processItem(child));
      }

      return processed;
    };

    return menu.map((item) => processItem(item));
  }

  /* eslint-disable no-useless-escape */
  normalizeRouteStatic(route: string): string {
    let normalized = route;

    const routeMatch = route.match(/^\/layout\/([^\/]+)/);
    const routeModule = routeMatch ? routeMatch[1] : null;

    const validModules = [
      'accounting',
      'tesorería',
      'tesoreria',
      'budget',
      'vault',
      'maestros',
      'invoicing',
    ];
    const hasValidModule = routeModule && validModules.includes(routeModule.toLowerCase());

    if (hasValidModule) {
      normalized = normalized.replace(/\/+/g, '/');
      if (!normalized.startsWith('/')) normalized = '/' + normalized;
      return normalized;
    }

    const activeModule = localStorage.getItem('selectedRoute');
    const targetModule = validModules.includes(activeModule || '')
      ? activeModule === 'tesoreria'
        ? 'tesorería'
        : activeModule || 'accounting'
      : 'accounting';

    normalized = normalized.replace(
      /\/layout\/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing)\/\1\//g,
      '/layout/$1/',
    );
    normalized = normalized.replace(/\/+/g, '/');
    if (!normalized.startsWith('/')) normalized = '/' + normalized;

    if (
      !normalized.match(
        /^\/layout\/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing)\//,
      )
    ) {
      const cleanPath = normalized
        .replace(/^\/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing)\//, '')
        .replace(/^\//, '');
      normalized = `/layout/${targetModule}/${cleanPath}`;
    }

    return normalized;
  }
  /* eslint-enable no-useless-escape */
  detectModule(route: string): string {
    const routeLower = route.toLowerCase();

    if (routeLower.includes('invoicing') || routeLower.includes('factura')) {
      return 'invoicing';
    }

    const budgetKeywords = [
      'budget',
      'presupuesto',
      'budget-parameters',
      'resource-type',
      'funding-source',
    ];

    const tesoreriaKeywords = [
      'tesorería',
      'tesoreria',
      'treasury',
      'bank',
      'bank-account',
      'pago',
      'payment',
      'banco',
      'resource-type',
      'embargos',
      'treasury-parameters',
    ];

    const vaultKeywords = [
      'vault',
      'dashboard',
      'ingest',
      'preservation',
      'retention',
      'legal-hold',
      'reports',
      'cloud',
      'notifications',
      'audit',
      'settings',
      'admin',
    ];

    if (budgetKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'budget';
    }

    if (tesoreriaKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'tesorería';
    }

    if (vaultKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'vault';
    }

    return 'accounting';
  }

  /** True si la ruta es la pestaña base (dashboard) del modulo, que no cuenta para el limite. */
  private _isModuleBaseRoute(route: string, module: string): boolean {
    const n = route
      .toLowerCase()
      .replace(/tesoreria/g, 'tesorería')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');
    return n === `/${module}` || n === `/layout/${module}`;
  }

  getRouteKey(route: string | undefined | null): string {
    if (!route) return '';

    let normalized = route.toLowerCase();
    normalized = normalized.split('?')[0].split('#')[0];
    normalized = normalized.replace(/\/+/g, '/');

    normalized = normalized.replace(/tesoreria/g, 'tesorería');
    normalized = normalized.replace(/\/layout\/(tesoreria)\//g, '/layout/tesorería/');

    return normalized;
  }

  updateBreadcrumbForMenuItem(item: any, route: string, _module: string): void {
    const cleanRoute = route.split('?')[0].split('#')[0];
    const parts = cleanRoute.split('/').filter((p) => p && p !== '');

    let startIndex = parts.indexOf('layout');
    if (startIndex !== -1) startIndex++;
    else startIndex = 0;

    let groupIcon = 'Folder';
    for (const menuItem of this.menuItems) {
      if (menuItem.label === 'Maestros Contabilidad' && menuItem.icon) {
        groupIcon = menuItem.icon;
        break;
      }
      if (menuItem.children) {
        for (const child of menuItem.children) {
          if (child.label === 'Maestros Contabilidad' && child.icon) {
            groupIcon = child.icon;
            break;
          }
        }
      }
    }

    const menuIcon = this.menuIconService.getIcon()();

    const breadcrumbItems: BreadcrumbItem[] = [];
    let accumulatedPath = '';

    for (let i = startIndex; i < parts.length; i++) {
      const segment = parts[i];
      const pathParts = parts.slice(0, i + 1);
      accumulatedPath = '/' + pathParts.join('/');

      const isLast = i === parts.length - 1;
      const label = this.getLabelForRouteSegment(segment);

      let icon: string;
      if (i === startIndex) {
        icon = 'Folder';
      } else if (i === startIndex + 1 && label === 'Maestros Contabilidad') {
        icon = groupIcon;
      } else if (i === parts.length - 2) {
        icon = menuIcon || 'File';
      } else {
        icon = 'File';
      }

      breadcrumbItems.push({
        label: label,
        route: isLast ? undefined : accumulatedPath,
        icon: icon,
      });
    }
    this.breadcrumbSync.update(breadcrumbItems);
  }

  /**
   * Obtiene el icono de un grupo del menú por su label
   */
  private getGroupIconFromMenu(groupLabel: string): string | null {
    for (const menuItem of this.menuItems) {
      if (menuItem.label === groupLabel && menuItem.icon) {
        return menuItem.icon;
      }
      if (menuItem.children) {
        for (const child of menuItem.children) {
          if (child.label === groupLabel && child.icon) {
            return child.icon;
          }
        }
      }
    }
    return null;
  }

  getLabelForRouteSegment(segment: string): string {
    const labels: Record<string, string> = {
      accounting: 'Contabilidad',
      tesorería: 'Tesorería',
      budget: 'Presupuesto',
      masters: 'Maestros',
      'cost-center': 'Centros de Costo',
      'cost-center-type': 'Tipos de Centro de Costo',
      'department-city': 'Departamentos y Ciudades',
      list: 'Listado',
      create: 'Crear',
      edit: 'Editar',
      detail: 'Detalle',
      view: 'Ver',
      dashboard: 'Inicio',
      ingest: 'Carga de Documentos',
      preservation: 'Preservacion Digital',
      retention: 'Retencion Documental',
      'legal-hold': 'Custodia Legal ',
      reports: 'Reportes Generales',
      cloud: 'Respaldo Externo',
      notifications: 'Notificaciones',
      audit: 'Historial de Actividad',
      settings: 'Configuracion General',
      admin: 'Administrador',
      tenants: 'Gestion de Empresas',
      permissions: 'Permisos',
      servers: 'Configuracion Servidores',
      'preservation-messages': 'Mensajes Informativos',
      'repository-admin': 'Boveda Digital',
      'accounting-parameters': 'Parámetros',
      'tipo-plan': 'Tipos de Plan',
      'tipo-plan-historico': 'Histórico de Tipos de Plan',
      dependency: 'Dependencias',
      'dian-concept': 'Conceptos Dian',
      'account-structure': 'Estructura de Cuentas',
      'concept-entry-type': 'Conceptos/Tipo Asiento',
      currency: 'Monedas',
      'account-plan': 'Plan Cuentas',
      'third-party': 'Terceros',
      'accounting-format': 'Formatos Contables',
      accrual: 'Causación Contable',
      'deferred-process': 'Proceso de Diferidos',
      'journal-entry': 'Asiento Contable',
      'accrual-approval/list': 'Aprobar Causación',
      'accrual-annulment/list': 'Anular Causación',
      'treasury-parameters': 'Parámetros',
      depreciation: 'Depreciación',
      'journal-entry-annulment': 'Anulación Asientos',
      'bank-account': 'Cuentas Bancarias',
      'other-discount': 'Otros Descuentos',
      'other-deductions': 'Otras Deducciones',
      deferred: 'Diferidos',
      'journal-entry-deferred': 'Diferidos',
      'accounting-concepts': 'Conceptos',
      'journal-entry-type': 'Tipo Asientos',
      'accounting-currency': 'Monedas',
      'exchange-rate': 'Tasas de Cambio',
    };

    if (labels[segment]) return labels[segment];

    if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-/.test(segment)) {
      return 'Detalle';
    }

    return segment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  onItemClick(item: any, domEvent?: Event): void {
    domEvent?.preventDefault();
    domEvent?.stopPropagation();

    if (item.icon) {
      this.menuIconService.setIcon(item.icon);
    }

    if (Array.isArray(item.children) && item.children.length > 0) {
      return;
    }

    let routeToUse = item.route;
    if (!routeToUse || typeof routeToUse !== 'string' || routeToUse.trim() === '') {
      return;
    }

    routeToUse = this.normalizeRouteStatic(routeToUse);

    const routeKey = this.getRouteKey(routeToUse);

    if (!isRouteValid(routeToUse)) {
      const module = getModuleFromRoute(routeToUse) || 'accounting';

      const moduleNames: { [key: string]: string } = {
        accounting: 'Contabilidad',
        tesorería: 'Tesorería',
        budget: 'Presupuesto',
        products: 'Productos',
        company: 'Empresas',
        vault: 'Vault',
        maestros: 'Configuraciones',
        invoicing: 'Facturación',
      };

      const moduleName = moduleNames[module];

      this.showSimpleToast(`"${item.label}" en ${moduleName} aún no está disponible`);

      return;
    }

    if (item?.label) {
      const title = item.label || item.name || item.title || 'Sin título';
      const icon = item.icon || undefined;
      const detectedModule = this.detectModule(routeToUse);

      const existingTab = this.navService.tabs().find((t) => {
        if (!t.route) return false;
        return this.getRouteKey(t.route) === routeKey;
      });

      if (existingTab && existingTab.id) {
        this.navService.selectTab(existingTab.id);
        this.updateBreadcrumbForMenuItem(item, routeToUse, detectedModule);
      } else {
        const openInModule = this.navService.tabs().filter((t) => {
          if (!t.route) return false;
          if (this.detectModule(t.route) !== detectedModule) return false;
          return !this._isModuleBaseRoute(t.route, detectedModule);
        }).length;

        if (openInModule >= MAX_OPEN_TABS) {
          this.showSimpleToast(
            `Máximo ${MAX_OPEN_TABS} pestañas abiertas. Cierra alguna para abrir otra.`,
            2500,
          );
          return;
        }

        this.navService.addTabFromMenu({
          label: title,
          route: routeToUse,
          icon: icon,
        } as MenuItem);

        this.updateBreadcrumbForMenuItem(item, routeToUse, detectedModule);
      }

      this.routeSelected.emit(routeToUse);

      const moduleKey = detectedModule as
        | 'accounting'
        | 'tesorería'
        | 'budget'
        | 'vault'
        | 'invoicing';

      const moduleInRoute = this._extractModuleFromRoute(routeToUse);

      const finalModuleKey =
        moduleInRoute && this._isValidModule(moduleInRoute)
          ? (moduleInRoute as typeof moduleKey)
          : moduleKey;

      this.routeSync.saveRoute(finalModuleKey, routeToUse);

      this.cdr.markForCheck();
    }
  }

  _extractModuleFromRoute(route: string): string | null {
    const cleanRoute = route.split('?')[0].split('#')[0];
    const parts = cleanRoute.split('/').filter((p) => p && p !== '');

    const layoutIndex = parts.indexOf('layout');
    if (layoutIndex !== -1 && parts[layoutIndex + 1]) {
      const moduleCandidate = parts[layoutIndex + 1];
      return moduleCandidate === 'tesoreria' ? 'tesorería' : moduleCandidate;
    }

    if (parts[0] && this._isValidModule(parts[0])) {
      const moduleCandidate = parts[0];
      return moduleCandidate === 'tesoreria' ? 'tesorería' : moduleCandidate;
    }

    return null;
  }

  _isValidModule(module: string): boolean {
    return [
      'accounting',
      'tesorería',
      'tesoreria',
      'budget',
      'vault',
      'maestros',
      'invoicing',
    ].includes(module);
  }

  onToggleSidebar(): void {
    const newCollapsedState = !this.collapsed();
    this.collapsedChange.emit(newCollapsedState);
    this.sidebarState.setState(!newCollapsedState, true);
  }

  ngOnDestroy(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
